
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { subscribeToMailchimp } from "@/utils/mailchimp";
import { NavigationBar } from "@/components/landing/NavigationBar";
import { HeroSection } from "@/components/landing/HeroSection";
import { BenefitsSection } from "@/components/landing/BenefitsSection";
import { LoadingSpinner } from "@/components/landing/LoadingSpinner";
import { useAuthRedirect } from "@/hooks/useAuthRedirect";

export default function Index() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isLoading } = useAuthRedirect(navigate);

  const handleLoginClick = () => {
    navigate("/auth", { replace: true });
  };

  const handleDemoClick = () => {
    navigate("/welcome?demo=true", { replace: true });
  };

  const handleSubmit = async (formData: {
    name: string;
    email: string;
    industry: string;
  }) => {
    setIsSubmitting(true);
    try {
      // Check if email exists without fetching all fields
      const { data: existingProfile, error } = await supabase
        .from('profiles')
        .select('email')
        .eq('email', formData.email)
        .maybeSingle();
        
      if (error) throw error;
      
      if (existingProfile) {
        toast({
          title: "Email Already Registered",
          description: "This email is already registered. Please log in instead.",
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      // Add to temp users table
      const { error: insertError } = await supabase
        .from('temp_users')
        .insert([{
          name: formData.name,
          email: formData.email,
          industry: formData.industry
        }]);
        
      if (insertError) throw insertError;

      // Subscribe to Mailchimp
      await subscribeToMailchimp({
        email: formData.email,
        firstName: formData.name.split(' ')[0],
        lastName: formData.name.split(' ').slice(1).join(' '),
        tags: ['Website Signup'],
        status: 'Subscribed'
      });

      // Set the email in localStorage before navigation
      localStorage.setItem('tempUserEmail', formData.email);
      
      toast({
        title: "Success!",
        description: "Your information has been saved."
      });
      
      navigate("/client-info", { replace: true });
    } catch (error: any) {
      console.error('Error during submission:', error);
      toast({
        title: "Error",
        description: error.message || "An error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    const formElement = document.querySelector('#signup-form');
    formElement?.scrollIntoView({ behavior: 'smooth' });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 relative">
      <NavigationBar 
        onLoginClick={handleLoginClick} 
        onDemoClick={handleDemoClick} 
      />
      
      <HeroSection 
        isSubmitting={isSubmitting} 
        onSubmit={handleSubmit} 
        onScrollToForm={scrollToForm} 
      />
      
      <BenefitsSection />
    </div>
  );
}
